// Sidebar navigation configs for all roles.
// Keep this file free of JSX; it only maps paths/labels/icons.
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Calendar,
  ClipboardList,
  UserCheck,
  CreditCard,
  Trophy,
  Star,
  Clock,
  DollarSign,
  User,
  FileText,
  Repeat,
  Briefcase,
  MapPin,
  UserPlus,
  Award,
  ArrowLeftRight,
  ArrowRightLeft,
  Tag,
  BookOpen,
  Wallet,
  Settings,
  Shirt,
  Wrench,
  Building2,
  Phone,
  Mail,
  MessageSquare,
  MessageCircle,
  HardDrive,
  CheckSquare,
  ToggleLeft,
  Image,
  ScrollText,
  Heart,
  Server,
  AlertTriangle,
  GitMerge,
  FolderOpen,
  FileUp,
  Bell,
  Sparkles,
  Tent,
  _History,
} from 'lucide-react';

export const adminNavGroups = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, type: 'single' },
  { path: '/admin/tournaments', label: 'Tournaments', icon: Award, type: 'single', planFeatureKey: 'tournaments.registrations' },
  // iter334ce — Events was leaking on lane-only tenants (Stelton).
  // Gate on `events.core`.
  { path: '/admin/events', label: 'Events', icon: Calendar, type: 'single', planFeatureKey: 'events.core' },
  {
    label: 'Lane Bookings',
    icon: MapPin,
    type: 'group',
    planFeatureKey: 'bookings.lanes',
    items: [
      { path: '/admin/all-bookings', label: 'All Bookings', icon: Calendar },
      { path: '/admin/lanes', label: 'Bookings', icon: Calendar },
      { path: '/admin/customers', label: 'Customers', icon: Users },
      { path: '/admin/lanes/maintenance', label: 'Maintenance', icon: Wrench },
      { path: '/admin/lanes/settings', label: 'Settings', icon: Settings },
    ],
  },
  {
    label: 'Coaching',
    icon: BookOpen,
    type: 'group',
    // iter334ce — Whole group depends on programs. Individual items
    // (Small Group / Camps) still carry their own feature keys.
    planFeatureKey: 'programs.catalog',
    items: [
      { path: '/admin/programs', label: 'Programs', icon: Trophy },
      { path: '/admin/classes', label: 'Classes', icon: Calendar, planFeatureKey: 'programs.classes' },
      { path: '/admin/small-group', label: 'Small Group Sessions', icon: Users, planFeatureKey: 'bookings.small_groups' },
      { path: '/admin/camps', label: 'Camps', icon: Calendar, planFeatureKey: 'camps.core' },
      { path: '/admin/attendance', label: 'Attendance', icon: CheckSquare, planFeatureKey: 'programs.attendance' },
    ],
  },
  {
    label: 'Coaches',
    icon: GraduationCap,
    type: 'group',
    // iter334ce — Coach mgmt only meaningful when programs.coaches is on
    planFeatureKey: 'programs.coaches',
    items: [
      { path: '/admin/coaches', label: 'Coaches', icon: GraduationCap },
      { path: '/admin/billing', label: 'Coach Billing', icon: DollarSign, planFeatureKey: 'programs.coach_billing' },
    ],
  },
  {
    label: 'Staff',
    icon: Briefcase,
    type: 'group',
    // iter334ce — Staff group requires core.staff feature
    planFeatureKey: 'core.staff',
    items: [
      { path: '/admin/staff', label: 'Staff', icon: Briefcase },
      { path: '/admin/staff-billing', label: 'Staff Billing', icon: DollarSign, planFeatureKey: 'staff.billing' },
      { path: '/admin/scheduling', label: 'Scheduling', icon: Clock, planFeatureKey: 'staff.scheduling' },
      { path: '/admin/shift-swaps', label: 'Shift Swaps', icon: ArrowLeftRight, planFeatureKey: 'staff.swap_shifts' },
    ],
  },
  {
    label: 'Enrollments',
    icon: ClipboardList,
    // iter335c — decision A: a lane-only tenant deals in "customers", not
    // parents/players/enrollments. Every child of this group is now plan-
    // gated on enrollments.core, so the whole Enrollments group disappears
    // for booking-only tenants (filterByFeatures drops empty groups).
    type: 'group',
    items: [
      { path: '/admin/enrollments', label: 'Enrollments', icon: ClipboardList, planFeatureKey: 'enrollments.core' },
      { path: '/admin/enrollment-requests', label: 'Enrollment Requests', icon: Repeat, planFeatureKey: 'enrollments.core' },
      { path: '/admin/parents', label: 'Parents', icon: UserCheck, planFeatureKey: 'enrollments.core' },
      { path: '/admin/players', label: 'Players', icon: Users, planFeatureKey: 'enrollments.core' },
      { path: '/admin/movement-requests', label: 'Movement Requests', icon: ArrowLeftRight, planFeatureKey: 'enrollments.core' },
      { path: '/admin/onboarding', label: 'Parent Onboarding', icon: UserPlus, planFeatureKey: 'enrollments.core' },
      { path: '/admin/trials', label: 'Trial Requests', icon: Star, planFeatureKey: 'enrollments.core' },
    ],
  },
  {
    label: 'Billing & Payments',
    icon: Wallet,
    type: 'group',
    // iter334ce — All items require the payments module. Lane-only
    // tenants HAVE this on (lane checkout needs it) so the group
    // still appears for them.
    planFeatureKey: 'payments.module',
    items: [
      { path: '/admin/billing-management', label: 'Billing Overview', icon: Wallet },
      { path: '/admin/aged-receivables', label: 'Aged Receivables', icon: AlertTriangle },
      { path: '/admin/transactions', label: 'Transactions', icon: ArrowRightLeft },
      { path: '/admin/refunds', label: 'Refunds', icon: DollarSign, planFeatureKey: 'payments.refunds' },
      { path: '/admin/invoice-management', label: 'Invoice Management', icon: FileText },
      { path: '/admin/credit-ledger', label: 'Credit Ledger', icon: Wallet, planFeatureKey: 'payments.account_credit' },
      { path: '/admin/discounts', label: 'Discounts', icon: Tag, planFeatureKey: 'enrollments.discounts' },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    type: 'group',
    items: [
      { path: '/admin/business-hours', label: 'Business Settings', icon: Building2 },
      { path: '/admin/payment-settings', label: 'Payment Settings', icon: CreditCard, planFeatureKey: 'payments.stripe_byok' },
      { path: '/admin/settings/locations', label: 'Locations', icon: MapPin },
      { path: '/admin/settings/features', label: 'Feature Toggles', icon: ToggleLeft },
      { path: '/admin/settings/homepage', label: 'Homepage Editor', icon: LayoutDashboard },
      { path: '/admin/settings/cron-jobs', label: 'Background Jobs', icon: Clock },
      { path: '/admin/settings/push-notifications', label: 'Push Notifications', icon: Bell },
    ],
  },
  {
    label: 'Communications',
    icon: Mail,
    type: 'group',
    planFeatureKey: 'comms.email',
    items: [
      { path: '/admin/email-templates', label: 'Email Templates', icon: Mail },
      { path: '/admin/email-campaigns', label: 'Email Campaigns', icon: Mail, staffAllowed: true, planFeatureKey: 'comms.email' },
      { path: '/admin/email-suppressions', label: 'Email Suppression List', icon: Mail, planFeatureKey: 'comms.email' },
      { path: '/admin/email-settings', label: 'Email Config', icon: Server },
      { path: '/admin/whatsapp', label: 'WhatsApp Inbox', icon: MessageSquare, showWaBadge: true, planFeatureKey: 'comms.whatsapp' },
      { path: '/team-chats', label: 'Team Chats', icon: MessageCircle, showChatBadge: true, planFeatureKey: 'comms.team_chats' },
    ],
  },
  {
    label: 'Data Management',
    icon: HardDrive,
    type: 'group',
    items: [
      { path: '/admin/users', label: 'User Management', icon: Users },
      { path: '/admin/duplicates', label: 'Merge Accounts', icon: GitMerge },
      { path: '/admin/documents', label: 'Legal Documents', icon: FileText },
      { path: '/admin/academy-documents', label: 'Academy Documents', icon: FolderOpen, planFeatureKey: 'enrollments.core' },
      { path: '/admin/hero-images', label: 'Hero Images & Gallery', icon: Image },
      { path: '/admin/splash', label: 'Splash Announcement', icon: Sparkles },
      { path: '/admin/blog', label: 'Blog / News', icon: FileText, planFeatureKey: 'content.blog' },
      { path: '/admin/uniform-order', label: 'Uniform Campaigns', icon: Shirt, planFeatureKey: 'commerce.uniforms' },
      { path: '/admin/document-campaigns', label: 'Document Campaigns', icon: FileUp, planFeatureKey: 'enrollments.core' },
      { path: '/admin/backup', label: 'Backup & Restore', icon: HardDrive },
    ],
  },
  {
    label: 'System Logs',
    icon: ScrollText,
    type: 'group',
    items: [
      { path: '/admin/audit-logs', label: 'Audit Logs', icon: ScrollText },
      { path: '/admin/error-logs', label: 'Error Logs', icon: AlertTriangle },
    ],
  },
  { path: '/admin/my-family', label: 'My Family', icon: Heart, type: 'single', planFeatureKey: 'enrollments.core' },
];

export const staffNavItems = [
  { path: '/staff', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/staff/tournaments', label: 'Tournaments', icon: Award, featureKey: 'tournaments' },
  { path: '/staff/schedule', label: 'My Schedule', icon: Calendar, featureKey: 'shift_scheduling' },
  { path: '/staff/shift-swaps', label: 'Shift Swaps', icon: ArrowLeftRight, featureKey: 'shift_scheduling' },
  { path: '/staff/hours', label: 'My Hours', icon: Clock, featureKey: 'log_hours' },
  { path: '/staff/enrollments', label: 'Enrollments', icon: ClipboardList, featureKey: 'enrollment_management' },
  { path: '/staff/trials', label: 'Trial Requests', icon: Star, featureKey: 'trial_management' },
  { path: '/staff/onboarding', label: 'Parent Onboarding', icon: UserPlus, featureKey: 'parent_onboarding' },
  // Gated by staff feature toggle `lane_administration`; when on, staff
  // gets the same lane-management pages admins have (bookings calendar,
  // maintenance, settings) with full CRUD permissions.
  {
    label: 'Lane Administration',
    icon: MapPin,
    featureKey: 'lane_administration',
    type: 'group',
    items: [
      { path: '/admin/all-bookings', label: 'All Bookings', icon: Calendar },
      { path: '/admin/lanes', label: 'Bookings', icon: Calendar },
      { path: '/admin/lanes/maintenance', label: 'Maintenance', icon: Wrench },
      { path: '/admin/lanes/settings', label: 'Settings', icon: Settings },
    ],
  },
  { path: '/staff/uniform-campaigns', label: 'Uniform Campaigns', icon: Shirt, featureKey: 'uniform_campaigns' },
  // Gated by staff feature toggle `billing_management`; when on, staff
  // gets full access to Transactions, Invoice Management, and Discounts
  // with the same functionality as admins.
  {
    label: 'Billing Management',
    icon: Wallet,
    featureKey: 'billing_management',
    type: 'group',
    items: [
      { path: '/admin/transactions', label: 'Transactions', icon: ArrowRightLeft },
      { path: '/admin/invoice-management', label: 'Invoice Management', icon: FileText },
      { path: '/admin/discounts', label: 'Discounts', icon: Tag },
    ],
  },
  { path: '/staff/academy-documents', label: 'Academy Documents', icon: FolderOpen },
  // Staff can send marketing campaigns using approved templates only.
  // Body is locked server-side — staff picks subject + audience + template.
  { path: '/admin/email-campaigns', label: 'Email Campaigns', icon: Mail, planFeatureKey: 'comms.email' },
  { path: '/admin/email-suppressions', label: 'Email Suppression List', icon: Mail, planFeatureKey: 'comms.email' },
  { path: '/team-chats', label: 'Team Chats', icon: MessageCircle, showChatBadge: true, planFeatureKey: 'comms.team_chats' },
  { path: '/staff/my-family', label: 'My Family', icon: Heart },
  { path: '/staff/profile', label: 'My Profile', icon: User },
];

export const coachNavItems = [
  { path: '/coach', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/coach/tournaments', label: 'Tournaments', icon: Award, featureKey: 'tournaments', showChatBadge: true },
  { path: '/coach/hours', label: 'My Hours', icon: Clock, featureKey: 'log_hours' },
  { path: '/coach/attendance', label: 'Attendance', icon: ClipboardList, featureKey: 'mark_attendance' },
  { path: '/coach/movement-requests', label: 'Movement Requests', icon: ArrowLeftRight, featureKey: 'movement_requests' },
  { path: '/coach/uniform-campaigns', label: 'Uniform Campaigns', icon: Shirt, featureKey: 'uniform_campaigns' },
  { path: '/coach/academy-documents', label: 'Academy Documents', icon: FolderOpen },
  { path: '/team-chats', label: 'Team Chats', icon: MessageCircle, showChatBadge: true, planFeatureKey: 'comms.team_chats' },
  { path: '/coach/my-family', label: 'My Family', icon: Heart },
  { path: '/coach/profile', label: 'My Profile', icon: User },
];

export const parentNavItems = [
  { path: '/parent', label: 'Dashboard', icon: LayoutDashboard, type: 'single' },
  { path: '/parent/billing', label: 'Billing', icon: CreditCard, featureKey: 'billing_portal', type: 'single' },
  { path: '/parent/notifications', label: 'Notifications', icon: Bell, showBadge: true, type: 'single' },
  { path: '/team-chats', label: 'Team Chats', icon: MessageCircle, showChatBadge: true, planFeatureKey: 'comms.team_chats', type: 'single' },
  { path: '/parent/tournaments', label: 'Tournaments', icon: Award, featureKey: 'tournaments', planFeatureKey: 'tournaments.registrations', showChatBadge: true, type: 'single' },
  { path: '/parent/tournaments?tab=schedule', label: 'Games', icon: Calendar, featureKey: 'tournaments', planFeatureKey: 'tournaments.registrations', type: 'single' },
  {
    label: 'Coaching',
    icon: BookOpen,
    type: 'group',
    items: [
      { path: '/parent/enrollments', label: 'Enrollments', icon: ClipboardList },
      { path: '/parent/small-group', label: 'Small Group Sessions', icon: Users, featureKey: 'small_group_sessions', planFeatureKey: 'bookings.small_groups' },
      { path: '/parent/camps', label: 'Camps', icon: Tent, featureKey: 'camp_registration', planFeatureKey: 'camps.core' },
      { path: '/parent/attendance', label: 'Attendance', icon: CheckSquare, featureKey: 'view_attendance' },
      { path: '/parent/programs', label: 'Programs', icon: GraduationCap },
    ],
  },
  { path: '/parent/book-lane', label: 'Book Lane', icon: MapPin, featureKey: 'lane_booking', planFeatureKey: 'bookings.lanes', type: 'single' },
  { path: '/parent/events', label: 'Events', icon: Calendar, featureKey: 'events', type: 'single' },
  {
    label: 'Documents',
    icon: FolderOpen,
    type: 'group',
    items: [
      { path: '/parent/academy-documents', label: 'Academy Documents', icon: FolderOpen },
      { path: '/parent/document-upload', label: 'Document Requests', icon: FileUp },
    ],
  },
  { path: '/parent/children', label: 'My Family', icon: Heart, type: 'single' },
  { path: '/parent/uniform-orders', label: 'My Uniform Orders', icon: Shirt, type: 'single' },
  { path: '/parent/contact', label: 'Contact Us', icon: Phone, type: 'single' },
];

export const customerNavItems = [
  { path: '/book', label: 'Book Lane', icon: MapPin },
  { path: '/programs', label: 'Programs', icon: GraduationCap },
  { path: '/customer/billing', label: 'Billing', icon: CreditCard, featureKey: 'billing_portal' },
  { path: '/customer/contact', label: 'Contact Us', icon: Phone },
];

export const playerNavItems = [
  { path: '/player', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/player/book-lane', label: 'Book Lane', icon: MapPin },
  { path: '/player/academy-documents', label: 'Academy Documents', icon: FolderOpen },
  { path: '/player/contact', label: 'Contact Us', icon: Phone },
];

/**
 * Resolve the base nav items (or groups) for a given role.
 */
export function getNavForRole(role) {
  switch (role) {
    case 'admin':
      return adminNavGroups;
    case 'staff':
      return staffNavItems;
    case 'coach':
      return coachNavItems;
    case 'customer':
      return customerNavItems;
    case 'player':
      return playerNavItems;
    default:
      return parentNavItems;
  }
}

/**
 * Filter items by feature flags. Admins see everything for ROLE features,
 * but plan-level features still hide nav (admins should not see UI for
 * things their tenant isn't subscribed to).
 *
 * Also filters grouped items: empty groups are removed.
 */
export function filterByFeatures(items, { isAdmin, isFeatureEnabled, isPlanFeatureEnabled }) {
  // Default to a passthrough so legacy call-sites (with no plan checker
  // wired in) keep working — they simply don't gate by plan.
  const planCheck = typeof isPlanFeatureEnabled === 'function'
    ? isPlanFeatureEnabled
    : () => true;
  return items
    .map((item) => {
      if (item.type === 'group' && Array.isArray(item.items)) {
        // Plan-level group gate runs BEFORE role check — a group hidden
        // by plan must disappear for admins too (they shouldn't see UI
        // for a category their tenant isn't subscribed to).
        if (item.planFeatureKey && !planCheck(item.planFeatureKey)) {
          return null;
        }
        // If the group itself has a featureKey (e.g. "Lane Administration"
        // gated by the staff `lane_administration` toggle), the whole group
        // disappears when disabled — even if individual child rows have no
        // featureKey of their own.
        if (item.featureKey && !isAdmin && !isFeatureEnabled(item.featureKey)) {
          return null;
        }
        const filteredChildren = item.items.filter((child) => {
          // Plan gate first (applies to admins too).
          if (child.planFeatureKey && !planCheck(child.planFeatureKey)) return false;
          // Role gate skipped for admins (they have all role features).
          if (isAdmin) return true;
          return !child.featureKey || isFeatureEnabled(child.featureKey);
        });
        if (filteredChildren.length === 0) return null;
        return { ...item, items: filteredChildren };
      }
      // Single-item rows.
      if (item.planFeatureKey && !planCheck(item.planFeatureKey)) return null;
      if (isAdmin) return item;
      if (!item.featureKey) return item;
      return isFeatureEnabled(item.featureKey) ? item : null;
    })
    .filter(Boolean);
}

// Plan feature required by the nav entry whose path is exactly `pathname`, or null.
export function planFeatureForPath(pathname) {
  let best = null;
  const visit = (item, groupKey) => {
    if (Array.isArray(item.items)) { item.items.forEach((c) => visit(c, item.planFeatureKey || groupKey)); return; }
    const key = item.planFeatureKey || groupKey;
    if (!item.path || !key) return;
    if (pathname.replace(/\/$/, '') === item.path && !best) best = { path: item.path, key };
  };
  [adminNavGroups, staffNavItems, coachNavItems, parentNavItems, customerNavItems, playerNavItems]
    .forEach((list) => list.forEach((i) => visit(i, null)));
  return best ? best.key : null;
}
